import { CHIP_LABEL } from "./format";
import type { AxisColumn } from "./AxisBar";

// Price band: optional stop-loss (red, left) and take-profit (green, right)
// bookends around the spot / strike / break-even points on a connecting rule.
type PriceLadderProps = {
  columns: AxisColumn[]; // centre points present (spot / strike / break-even)
  stopLoss?: string | null; // omitted ⇒ no left bookend
  takeProfit?: string | null; // omitted ⇒ no right bookend
};

// fixed-width (w-28) end-cap so both sides stay symmetric regardless of value length
const Bookend = ({ label, value, tone, tint }: { label: string; value: string; tone: string; tint: string }) => (
  <div className={`flex w-28 shrink-0 flex-col items-center justify-center gap-1.5 px-2 py-3 text-center ${tint}`}>
    <span className={`text-xs uppercase tracking-wide ${tone}`}>{label}</span>
    <span className={`text-sm font-semibold ${tone}`}>{value}</span>
  </div>
);

const PriceLadder = ({ columns, stopLoss, takeProfit }: PriceLadderProps) => {
  // centred columns put the outer dot centres 50/N % in so the rule meets them exactly
  const inset = columns.length ? `${50 / columns.length}%` : "50%";

  return (
    <div className="flex items-stretch overflow-hidden rounded-lg bg-tgpt-secondary-accentLight">
      {stopLoss && <Bookend label="Stop Loss" value={stopLoss} tone="text-error-main" tint="bg-error-accentLight" />}

      <div className="flex flex-1 flex-col gap-1.5 px-4 py-3">
        <div className="flex">
          {columns.map(c => (
            <span key={c.label} className="flex-1 text-center text-xs uppercase tracking-wide text-token-text-secondary">
              {c.label}
            </span>
          ))}
        </div>
        <div className="relative flex">
          <div
            className="pointer-events-none absolute top-1/2 h-px -translate-y-1/2 bg-mui-black-23 dark:bg-mui-white-38"
            style={{ left: inset, right: inset }}
          />
          {columns.map(c => (
            <div key={c.label} className="flex flex-1 justify-center">
              <span className="relative h-2.5 w-2.5 rounded-full bg-mui-black-23 dark:bg-mui-white-38" />
            </div>
          ))}
        </div>
        <div className="flex">
          {columns.map(c => (
            <span key={c.label} className={`flex-1 text-center ${CHIP_LABEL} text-token-text-primary`}>
              {c.value}
            </span>
          ))}
        </div>
      </div>

      {takeProfit && <Bookend label="Take Profit" value={takeProfit} tone="text-success-main" tint="bg-success-accentLight" />}
    </div>
  );
};

export default PriceLadder;
